/** Approximate FX for INR → USD display (update if you publish a live rate) */
export const INR_PER_USD = 83;

/** Safely parse numeric values from numbers, formatted strings (e.g. "48,300" or "₹48,300"), or null/undefined */
export function parseNumericVal(val: number | string | null | undefined): number {
  if (val === null || val === undefined) return 0;
  if (typeof val === "number") return Number.isFinite(val) ? val : 0;
  const cleaned = String(val).replace(/[^0-9.-]/g, "");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}

/** Full INR amount → USD millions */
export function inrToUsdMillions(inr: number | string): number {
  const n = parseNumericVal(inr);
  return n / INR_PER_USD / 1_000_000;
}

/** Indian crore count → USD millions (1 Cr = ₹10M) */
export function croreToUsdMillions(crores: number | string): number {
  const n = parseNumericVal(crores);
  return (n * 10) / INR_PER_USD;
}

/** Format a USD millions figure, e.g. $60M */
export function formatUsdMillions(
  usdMillions: number,
  options?: { decimals?: number; plus?: boolean }
): string {
  const decimals =
    options?.decimals ?? (Math.abs(usdMillions) >= 10 ? 0 : Math.abs(usdMillions) >= 1 ? 1 : 2);
  const core = `$${usdMillions.toFixed(decimals)}M`;
  return options?.plus ? `${core}+` : core;
}

/** Format stored INR (rupees) as USD millions */
export function formatInrAsUsdMillions(inr: number | string, decimals?: number): string {
  return formatUsdMillions(inrToUsdMillions(inr), { decimals });
}

/** INR for investor portal — full rupee amount (en-IN grouping) */
export function formatInr(inr: number | string, maximumFractionDigits = 0): string {
  const n = parseNumericVal(inr);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits,
    minimumFractionDigits: 0,
  }).format(n);
}

/** INR compact: Cr / L for large balances (investor charts & summary cards) */
export function formatInrCompact(inr: number | string): string {
  const n = parseNumericVal(inr);
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs >= 10_000_000) {
    const cr = abs / 10_000_000;
    const decimals = cr >= 100 ? 0 : cr >= 10 ? 1 : 2;
    return `${sign}₹${cr.toFixed(decimals)} Cr`;
  }
  if (abs >= 100_000) {
    const lakh = abs / 100_000;
    const decimals = lakh >= 100 ? 0 : 1;
    return `${sign}₹${lakh.toFixed(decimals)} L`;
  }
  return formatInr(n);
}

/** Chart axis labels for INR amounts */
export function formatInrAxis(inr: number | string): string {
  const n = parseNumericVal(inr);
  const abs = Math.abs(n);
  if (abs >= 10_000_000) return `₹${(abs / 10_000_000).toFixed(0)} Cr`;
  if (abs >= 100_000) return `₹${(abs / 100_000).toFixed(0)} L`;
  if (abs >= 1_000) return `₹${(abs / 1_000).toFixed(0)}K`;
  return `₹${abs.toFixed(0)}`;
}

/** Admin & public site — stored INR → USD millions */
export function formatMoney(inr: number | string): string {
  const n = parseNumericVal(inr);

  const usdM = inrToUsdMillions(n);
  if (usdM >= 1) {
    return formatUsdMillions(usdM, { decimals: usdM >= 10 ? 1 : 2 });
  }

  const usd = n / INR_PER_USD;
  if (usd >= 1_000_000) return formatUsdMillions(usd / 1_000_000);
  if (usd >= 1_000) return `$${(usd / 1_000).toFixed(1)}K`;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(usd);
}

/** Intl USD currency from INR (full dollars, not forced to millions) */
export function formatUsd(inr: number | string, maximumFractionDigits = 0): string {
  const n = parseNumericVal(inr);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits,
  }).format(n / INR_PER_USD);
}

/** Static marketing label from crore figure */
export function croreLabel(crores: number | string, plus = false): string {
  return formatUsdMillions(croreToUsdMillions(crores), { plus });
}

/**
 * NAV AUM has been stored in different units over time:
 * - INR rupees (seed / legacy, e.g. 50_000_000+)
 * - Indian crores (legacy mock NAV, ~400–600)
 * - USD millions (legacy admin input)
 */
export function normalizeAumToUsdMillions(raw: number | string): number {
  const n = parseNumericVal(raw);
  if (n <= 0) return 0;
  if (n >= 1_000_000) return inrToUsdMillions(n);
  if (n >= 400 && n <= 600) return croreToUsdMillions(n);
  return n;
}

/** Same rules as above, normalized to INR rupees for platform dashboards */
export function normalizeAumToInr(raw: number | string): number {
  const n = parseNumericVal(raw);
  if (n <= 0) return 0;
  if (n >= 1_000_000) return n;
  if (n >= 400 && n <= 600) return n * 10_000_000;
  return n * INR_PER_USD * 1_000_000;
}

/** Public / marketing — NAV AUM in USD millions */
export function formatNavAum(raw: number | string): string {
  return formatUsdMillions(normalizeAumToUsdMillions(raw));
}

/** Platform (admin) — NAV AUM in INR */
export function formatPlatformAum(raw: number | string): string {
  return formatInrCompact(normalizeAumToInr(raw));
}

/** Public — per-unit NAV as USD (legacy) */
export function formatNav(nav: number | string): string {
  const n = parseNumericVal(nav);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

/** Platform — per-unit NAV in INR */
export function formatPlatformNav(nav: number | string): string {
  return formatInr(nav, 2);
}

/** Platform dashboards: summary cards & tables (stored rupees in DB) */
export const formatPlatformCurrency = formatInrCompact;
export const formatPlatformCurrencyExact = formatInr;

